import {
	CARGO_TYPE, LTL_FAMILY_CARGO_TYPES, OTHER_FAMILY_CARGO_TYPES, TCargoEOS, TCargoRow, TReorderCargoRow, TReorderCargoRowCount,
	TReorderCargoRowIssue
} from '@types';
import { getCargoTypeFromCargoTypeId } from './getCargoTypeFromCargoTypeId';

/* A row's own type when it is a less-than-load row (Bundle, Pallets, Skid or Other), null for anything else. */
export const getReorderRowCargoType = (cargo: TCargoEOS): CARGO_TYPE | null => {
	const cargoType = getCargoTypeFromCargoTypeId(cargo?.cargo_type_id) as CARGO_TYPE;
	return LTL_FAMILY_CARGO_TYPES.includes(cargoType) ? cargoType : null;
};

/* A reorder states each freight line on its own when the shipment holds a Bundle or Skid row, or rows of different
   types (CON-1298). Every other reorder - one cargo, a multi-line Other or multi-row Pallets shipment, MachShip,
   custom items - keeps the single-cargo form exactly as it was. */
export const isPerRowReorder = (cargos: TCargoEOS[] = []): boolean => {
	const rowTypes = cargos.map(getReorderRowCargoType);
	if (!rowTypes.length || rowTypes.includes(null)) return false;
	const hasBundleOrSkid = rowTypes.some((rowType) => rowType === CARGO_TYPE.BUNDLE || rowType === CARGO_TYPE.SKID);
	return hasBundleOrSkid || new Set(rowTypes).size > 1;
};

// A non-Pallets row keeps pallet_qty equal to its quantity (qty), so qty is the count for every row type.
const getBookedCounts = (cargo: TCargoEOS): Omit<TReorderCargoRow, 'totalWeight'> => ({
	palletQty: Number(cargo?.pallet_qty) || 0,
	palletSpaces: Number(cargo?.qty) || 0,
	quantity: Number(cargo?.qty) || 0
});

// A Pallets row weighs by its pallets (its spaces when booked without a pallet count), any other row by its quantity.
const getRowUnits = (cargo: TCargoEOS, counts: Omit<TReorderCargoRow, 'totalWeight'>) => (
	getReorderRowCargoType(cargo) === CARGO_TYPE.PALLETS ? (counts.palletQty || counts.palletSpaces) : counts.quantity
);

/* Each row's figures as it was booked: where a per-row reorder starts. */
export const getReorderCargoRows = (cargos: TCargoEOS[] = []): TReorderCargoRow[] => cargos.map((cargo) => ({
	...getBookedCounts(cargo),
	totalWeight: Number(cargo?.total_weight) || 0
}));

/* Sets one of a row's counts and rescales its weight at the weight per unit it was booked with, so changing how many
   are sent never changes what each one weighs. A row booked without any units keeps its booked weight. */
export const setReorderCargoRowCount = (
	cargo: TCargoEOS,
	row: TReorderCargoRow,
	count: TReorderCargoRowCount,
	value: number
): TReorderCargoRow => {
	const updated = { ...row, [count]: Number(value) || 0 };
	const bookedWeight = Number(cargo?.total_weight) || 0;
	const bookedUnits = getRowUnits(cargo, getBookedCounts(cargo));
	const totalWeight = bookedUnits
		? Math.round((bookedWeight / bookedUnits) * getRowUnits(cargo, updated) * 100) / 100
		: bookedWeight;
	return { ...updated, totalWeight };
};

/* What's wrong with each row of a per-row reorder, each issue on the count it is about. Its path is the name
   ReorderCargoRows gives that field (cargoRows.<row>.<count>), so the error shows beside it. A Pallets row needs a
   pallet and a space, and no more pallets than palletsPerSpace allows; any other row needs a quantity. */
export const getReorderCargoRowIssues = (
	cargos: TCargoEOS[] = [],
	cargoRows: TReorderCargoRow[] = [],
	palletsPerSpace = 20
): TReorderCargoRowIssue[] => cargoRows.flatMap((row, i) => {
	const issue = (count: TReorderCargoRowCount, message: string): TReorderCargoRowIssue => ({ path: ['cargoRows', i, count], message });
	if (getReorderRowCargoType(cargos[i]) !== CARGO_TYPE.PALLETS) {
		return row.quantity < 1 ? [issue('quantity', 'Quantity must be greater than 0')] : [];
	}
	return [
		...(row.palletQty < 1 ? [issue('palletQty', 'Pallet Qty must be greater than 0')] : []),
		...(row.palletSpaces < 1 ? [issue('palletSpaces', 'Pallet Spaces must be greater than 0')] : []),
		...(row.palletQty > (row.palletSpaces * palletsPerSpace)
			? [issue('palletQty', `Pallet Qty must not exceed ${palletsPerSpace} pallets per space.`)]
			: []),
	];
});

/* The shipment's display rows (getCargoRows) as a per-row reorder will book them: each row's counts and weight as
   entered, its volume scaled by the same number of units. A Pallets row shows its spaces as qty and its pallets as
   items, any other row its quantity as both. Without form rows the booked rows are returned unchanged. */
export const getReorderDisplayRows = (rows: TCargoRow[], formRows?: TReorderCargoRow[]): TCargoRow[] => {
	if (!formRows?.length) return rows;
	return rows.map((row, i) => {
		const formRow = formRows[i];
		if (!formRow) return row;
		const isQuantityRow = OTHER_FAMILY_CARGO_TYPES.includes(row.type);
		const items = isQuantityRow ? formRow.quantity : (formRow.palletQty || formRow.palletSpaces);
		return {
			...row,
			qty: isQuantityRow ? formRow.quantity : formRow.palletSpaces,
			items,
			weight: formRow.totalWeight,
			cubic: row.items ? (row.cubic / row.items) * items : row.cubic,
		};
	});
};
